import { Fragment } from "react";
import { T } from "../theme";

// WizardChrome.jsx — wizard header band + condensed chrome bar
// (design_handoff_chrome_collapse, Option A "condense on scroll").
//
// The header band sits under the project tabs and carries the wizard's only
// navigation controls — there is no fixed bottom Back/Next bar anymore. While
// the single scroll container is scrolled past 160px, the compact bar slides
// in as an absolute overlay and keeps the same context + actions on screen.

const WIZARD_STEPS = ["Upload", "Map", "Validate", "Push"];

// Mini wizard steps: done = green check, current = teal dot + bold, upcoming
// muted. Completed steps are clickable to navigate back.
export function MiniSteps({ current = 1, onStep, size = "sm" }) {
  const fz = size === "md" ? 12 : 11;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
      {WIZARD_STEPS.map((s, i) => {
        const n = i + 1;
        const done = n < current;
        const cur = n === current;
        const clickable = done && onStep;
        return (
          <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <span
              onClick={() => clickable && onStep(n)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                font: `${cur ? 700 : 500} ${fz}px/14px ${T.fontSans}`,
                color: cur ? T.brand : done ? T.greenDark : T.inkSoft,
                cursor: clickable ? "pointer" : "default", whiteSpace: "nowrap",
              }}
            >
              {done && <i className="fa-solid fa-circle-check" style={{ fontSize: fz - 1 }} />}
              {cur && <span style={{ width: 6, height: 6, borderRadius: T.radiusPill, background: T.brand, display: "inline-block" }} />}
              {s}
            </span>
            {i < WIZARD_STEPS.length - 1 && <i className="fa-solid fa-chevron-right" style={{ fontSize: 8, color: T.borderStrong }} />}
          </span>
        );
      })}
    </div>
  );
}

// White band under the project tabs: crumb + step title, inline mini steps,
// and the wizard's Back / primary actions at the right. Scrolls away with the
// rest of the chrome.
export function WizardHeaderBand({ crumbs, title, current, onStep, extras, actions }) {
  return (
    <div style={{ background: T.white, borderBottom: `1px solid ${T.borderSubtle}`, padding: "10px 24px", display: "flex", alignItems: "center", gap: 20 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ font: `400 11px/14px ${T.fontSans}`, color: T.inkSoft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {crumbs.join(" / ")}
        </div>
        <div style={{ font: `700 17px/22px ${T.fontSans}`, color: T.ink, whiteSpace: "nowrap" }}>{title}</div>
      </div>
      <MiniSteps size="md" current={current} onStep={onStep} />
      {extras}
      <div style={{ flex: 1 }} />
      {actions}
    </div>
  );
}

// Single condensed chrome bar — absolute overlay at the top of the wizard's
// scroll region. Slides in once the chrome (top bar + project header + tabs +
// header band) has scrolled away; the sticky spreadsheet preview pins at
// top: 48 beneath it.
export function WizardCompactBar({ visible, crumbs, title, chip, current, onStep, extras, actions }) {
  return (
    <div style={{
      position: "absolute", top: 0, left: 0, right: 0, zIndex: 30,
      height: 48, background: T.white, borderBottom: `1px solid ${T.borderSubtle}`,
      boxShadow: visible ? T.shadowMd : "none",
      display: "flex", alignItems: "center", gap: 14, padding: "0 16px",
      transform: visible ? "translateY(0)" : "translateY(-105%)",
      visibility: visible ? "visible" : "hidden",
      transition: visible ? "transform 180ms ease-out" : "transform 180ms ease-out, visibility 0s 180ms",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ font: `400 10px/12px ${T.fontSans}`, color: T.inkSoft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {crumbs.join(" / ")}
        </div>
        <div style={{ font: `700 14px/18px ${T.fontSans}`, color: T.ink, whiteSpace: "nowrap" }}>{title}</div>
      </div>
      <div style={{ width: 1, height: 22, background: T.borderSubtle, flexShrink: 0 }} />
      {chip && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 24, padding: "0 10px", borderRadius: T.radiusPill, border: `1px solid ${T.borderSubtle}`, background: T.bg, flexShrink: 0 }}>
          <span style={{ width: 7, height: 7, borderRadius: T.radiusPill, background: T.accentOrange, display: "inline-block" }} />
          <span style={{ font: `600 12px/16px ${T.fontSans}`, color: T.ink }}>{chip.label}</span>
          {chip.note && <span style={{ font: `500 11px/16px ${T.fontSans}`, color: T.inkSoft }}>{chip.note}</span>}
        </span>
      )}
      <MiniSteps current={current} onStep={onStep} />
      {extras}
      <div style={{ flex: 1 }} />
      {actions}
    </div>
  );
}
